import { useEffect, useRef } from 'react';

/**
 * Scroll-reveal hook using IntersectionObserver.
 * Attach the returned ref to any DOM node with the `.reveal` class (or variants).
 * When the element enters the viewport, the `is-visible` class is added and animations trigger.
 * @param {Object} opts
 * @param {number} [opts.threshold=0.15] - fraction visible before triggering
 * @param {string} [opts.rootMargin='0px 0px -80px 0px']
 * @param {boolean} [opts.once=true] - only reveal once
 */
export default function useReveal({ threshold = 0.15, rootMargin = '0px 0px -80px 0px', once = true } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respect users who prefer reduced motion
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      el.classList.add('is-visible');
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, rootMargin, once]);
  return ref;
}

/**
 * Wraps children in a div that reveals on scroll.
 * Pass `delay` (ms) to stagger, and `as` to change the tag.
 */
export function Reveal({ children, className = '', variant = 'up', delay = 0, as: Tag = 'div', ...rest }) {
  const ref = useReveal();
  const style = delay ? { transitionDelay: `${delay}ms`, animationDelay: `${delay}ms` } : undefined;
  return (
    <Tag ref={ref} className={`reveal reveal-${variant} ${className}`} style={style} {...rest}>
      {children}
    </Tag>
  );
}
